import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import '../styles/CreateApp.css';
import './CodeGeneratorPage.css'; // 导入页面生成器样式
import { Markdown } from '../components/markdown/markdown';

// 页面生成器相关的接口
interface Message {
  type: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface StreamResponse {
  category: string;
  content: string;
}

interface PreviewResponse {
  filename: string;
  full_path: string;
}

interface UserResponse {
  default_workspace_id: string;
  [key: string]: any;
}

interface AppForm {
  app_name: string;
  description: string;
  app_status: string;
  workspace_id: string;
  app_json: {
    requirements: string[];
  };
  requirements_analysis?: string;
  user_stories?: string;
  specification?: string;
  generated_code?: string; // 添加生成的页面字段
}

interface UploadedFile {
  temp_file_id: string;
  minio_path: string;
  file_name: string;
  file_type: string;
}

interface CreateAppResponse {
  app_id: number;
  app_name: string;
  app_description: string;
  app_status: string;
  workspace_id: number;
  creator_user_id: number;
  create_time: string;
  update_time: string;
}

interface AppDetailResponse {
  app_id: number;
  app_name: string;
  app_description: string;
  app_status: string;
  workspace_id: number;
  creator_user_id: number;
  create_time: string;
  update_time: string;
  // ...其他属性
}

interface TaskStatusResponse {
  status: string;
  progress: number;
  message: string;
}

interface FlowResponse {
  record_id: number;
  text?: string;
  is_finished: boolean;
  describe?: string; // 添加describe字段
}

// 添加允许的文件类型常量
const ALLOWED_FILE_TYPES = [
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'application/pdf',
  'text/html',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml'
];

// 添加允许的文件扩展名
const ALLOWED_FILE_EXTENSIONS = [
  '.txt',
  '.md',
  '.mdx',
  '.pdf',
  '.html',
  '.xlsx',
  '.xls',
  '.docx',
  '.csv',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.svg'
];

// 添加文件大小限制常量（15MB）
const MAX_FILE_SIZE = 15 * 1024 * 1024;

// 添加文件数量限制常量
const MAX_FILE_COUNT = 5;

const CreateApp: React.FC = () => {
  const [step, setStep] = useState(1);
  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [formData, setFormData] = useState<AppForm>({
    app_name: '',
    description: '',
    app_status: 'active',
    workspace_id: '',
    app_json: {
      requirements: []
    }
  });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [error, setError] = useState('');
  const [analysisResult, setAnalysisResult] = useState<string>('');
  const [userStoriesResult, setUserStoriesResult] = useState<string>('');
  const [specificationResult, setSpecificationResult] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUserStoriesAnalyzing, setIsUserStoriesAnalyzing] = useState(false);
  const [isSpecificationAnalyzing, setIsSpecificationAnalyzing] = useState(false);
  const [recordId, setRecordId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [currentEditType, setCurrentEditType] = useState<'requirement' | 'user_stories' | 'specification'>('requirement');
  
  // 添加创建应用的状态
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const [appId, setAppId] = useState<number | null>(null);
  const [appCreationCompleted, setAppCreationCompleted] = useState(false);
  const [appDetail, setAppDetail] = useState<AppDetailResponse | null>(null);
  const [creationProgress, setCreationProgress] = useState(0);
  
  // 添加sessionid状态
  const [sessionId, setSessionId] = useState<string>('');
  
  // 页面生成相关状态
  const [codeMessages, setCodeMessages] = useState<Message[]>([]);
  const [codeInputText, setCodeInputText] = useState<string>('');
  const [isCodeLoading, setIsCodeLoading] = useState<boolean>(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [generatedCode, setGeneratedCode] = useState<string>('');
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isGeneratingPreview, setIsGeneratingPreview] = useState<boolean>(false);
  // 新增：右侧面板显示模式控制
  const [rightPanelMode, setRightPanelMode] = useState<'code' | 'preview'>('code');
  
  // refs
  const codeMessagesEndRef = useRef<HTMLDivElement>(null);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    // 从 URL 获取工作空间 ID
    const params = new URLSearchParams(location.search);
    const urlWorkspaceId = params.get('workspace_id');

    if (urlWorkspaceId) {
      setWorkspaceId(urlWorkspaceId);
      setFormData(prev => ({
        ...prev,
        workspace_id: urlWorkspaceId
      }));
    } else {
      // 如果 URL 中没有工作空间 ID，则获取默认工作空间
      const fetchUserInfo = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get<UserResponse>('https://ceshi.lightark.cc/api/v1/users/me', {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setWorkspaceId(response.data.default_workspace_id);
          setFormData(prev => ({
            ...prev,
            workspace_id: response.data.default_workspace_id
          }));
        } catch (err) {
          navigate('/apps'); // 如果获取失败，返回应用列表页面
        }
      };

      fetchUserInfo();
    }
  }, [location, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRequirementsChange = (files: FileList | null) => {
    if (files) {
      const fileNames = Array.from(files).map(file => file.name);
      setFormData(prev => ({
        ...prev,
        app_json: {
          ...prev.app_json,
          requirements: fileNames
        }
      }));
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    await uploadFile(files[0]);
  };

  const uploadFile = async (file: File) => {
    // 检查文件数量限制
    if (uploadedFiles.length >= MAX_FILE_COUNT) {
      setUploadError('最多只能上传5个文件');
      return;
    }

    // 获取文件扩展名
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    // 验证文件类型和扩展名
    if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
      setUploadError('不支持的文件类型。请上传以下格式的文件：txt、markdown、md、mdx、pdf、html、xlsx、xls、docx、csv、jpg、jpeg、png、gif、webp、svg');
      return;
    }

    // 验证文件大小
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('文件大小不能超过15MB');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post<UploadedFile>(
        'https://ceshi.lightark.cc/api/v1/files/upload/temp',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      setUploadedFiles(prev => [...prev, {
        ...response.data,
        file_name: file.name,
        file_type: file.type
      }]);

      // 更新 formData 中的 requirements
      setFormData(prev => ({
        ...prev,
        app_json: {
          ...prev.app_json,
          requirements: [...prev.app_json.requirements, response.data.temp_file_id]
        }
      }));
    } catch (err) {
      setUploadError('文件上传失败，请重试');
      console.error('文件上传失败:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const uploadArea = e.currentTarget;
    uploadArea.classList.add('dragover');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const uploadArea = e.currentTarget;
    uploadArea.classList.remove('dragover');
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const uploadArea = e.currentTarget;
    uploadArea.classList.remove('dragover');

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // 检查文件数量限制
      if (uploadedFiles.length >= MAX_FILE_COUNT) {
        setUploadError('最多只能上传5个文件');
        return;
      }

      // 获取文件扩展名
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      // 验证文件类型和扩展名
      if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
        setUploadError('不支持的文件类型。请上传以下格式的文件：txt、markdown、md、mdx、pdf、html、xlsx、xls、docx、csv、jpg、jpeg、png、gif、webp、svg');
        return;
      }

      // 验证文件大小
      if (file.size > MAX_FILE_SIZE) {
        setUploadError('文件大小不能超过15MB');
        return;
      }

      await uploadFile(file);
    }
  };

  const handleRemoveFile = (tempFileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.temp_file_id !== tempFileId));
    setFormData(prev => ({
      ...prev,
      app_json: {
        ...prev.app_json,
        requirements: prev.app_json.requirements.filter(id => id !== tempFileId)
      }
    }));
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('text') || fileType.includes('markdown')) return '📝';
    return '📎';
  };

  const handleNext = () => {
    if (!workspaceId) {
      navigate('/apps');
      return;
    }

    if (step === 1) {
      setStep(2);
      setError('');
    } else if (step === 2) {
      setStep(3);
      setError('');
      if (!analysisResult) {
        startAnalysis('requirement');
      }
    } else if (step === 3) {
      setStep(4);
      setError('');
      if (!userStoriesResult) {
        startAnalysis('user_stories');
      }
    } else if (step === 4) {
      setStep(5);
      setError('');
      if (!specificationResult) {
        startAnalysis('specification');
      }
    } else if (step === 5) {
      setStep(6);
      setError('');
      // 进入页面生成步骤时，根据已有信息提供建议
      if (specificationResult && !codeInputText) {
        setCodeInputText(`基于以下技术规范生成完整的应用页面：\n\n${formData.app_name} - ${formData.description}\n\n请生成一个完整的可运行的应用。`);
      }
    } else if (step === 6) {
      // 保存生成的页面到表单数据
      if (generatedCode) {
        setFormData(prev => ({
          ...prev,
          generated_code: generatedCode
        }));
      }
      setStep(7);
      setError('');
    } else if (step === 7) {
      handleSubmit();
    } else {
      navigate(`/apps?workspace_id=${workspaceId}`);
    }
  };

  const handleSubmit = async () => {
    if (!workspaceId) {
      navigate('/apps'); // 如果未选择工作空间，返回应用列表页面
      return;
    }

    // 设置创建应用状态为true，开始显示加载特效
    setIsCreatingApp(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post<CreateAppResponse>('https://ceshi.lightark.cc/api/v1/apps/', 
        {
          app_name: formData.app_name,
          app_description: formData.description,
          app_status: 'active',
          workspace_id: workspaceId,
          app_json: {
            version: "1.0.0",
            config: {},
            requirements: formData.app_json.requirements,
            requirement_content: analysisResult,
            user_stories: userStoriesResult,
            specification: specificationResult,
            web_html: generatedCode || ''
          }
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data && response.data.app_id) {
        // 保存应用ID，而不是立即导航
        setAppId(response.data.app_id);
        // 启动轮询获取应用创建状态
      } else {
        setError(t('createApp.errors.createFailed'));
        setIsCreatingApp(false);
      }
    } catch (err) {
      setError(t('createApp.errors.inputError'));
      console.error('创建应用失败:', err);
      setIsCreatingApp(false);
    }
  };

  // 轮询获取应用创建状态
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const fetchAppStatus = async () => {
      if (!appId) return;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get<TaskStatusResponse>(
          `https://ceshi.lightark.cc/api/v1/apps/${appId}/latest_task/status`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        // 更新创建进度
        if (response.data.progress) {
          setCreationProgress(response.data.progress);
        }

        // 如果应用创建完成
        if (response.data.status === 'completed') {
          setAppCreationCompleted(true);
          
          // 获取应用详情
          const detailResponse = await axios.get<AppDetailResponse>(
            `https://ceshi.lightark.cc/api/v1/apps/${appId}/detail`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          setAppDetail(detailResponse.data);
          
          // 清除轮询
          if (intervalId) {
            clearInterval(intervalId);
          }
          
          // 显示成功提示2秒后自动跳转
          setTimeout(() => {
            navigate(`/apps/detail/${appId}?workspace_id=${workspaceId}`);
          }, 2000);
        }
      } catch (err) {
        console.error('获取应用状态失败:', err);
        // 如果发生错误，不要停止轮询，继续尝试
      }
    };

    if (appId && isCreatingApp && !appCreationCompleted) {
      // 每2秒轮询一次
      intervalId = setInterval(fetchAppStatus, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [appId, isCreatingApp, appCreationCompleted, workspaceId, navigate]);

  // 处理进入应用
  const handleEnterApp = () => {
    if (appId && workspaceId) {
      navigate(`/apps/detail/${appId}?workspace_id=${workspaceId}`);
    }
  };

  const handleStepClick = (stepNumber: number) => {
    // 如果正在创建应用，禁止切换步骤
    if (isCreatingApp) return;
    
    // 允许点击所有步骤，包括第7步（创建应用）
    setStep(stepNumber);
  };

  // 修改滚动逻辑，只在内容生成过程中滚动
  useEffect(() => {
    // 仅当内容正在生成时才滚动
    if (!isAnalyzing && !isUserStoriesAnalyzing && !isSpecificationAnalyzing) {
      return; // 如果没有内容在生成，不进行滚动
    }

    // 滚动到正在生成的内容
    const scrollToGeneratingContent = () => {
      // 确定当前正在生成内容的容器ID
      let containerId = '';
      if (isAnalyzing) {
        containerId = 'analysis-content';
      } else if (isUserStoriesAnalyzing) {
        containerId = 'user-stories-content';
      } else if (isSpecificationAnalyzing) {
        containerId = 'specification-content';
      } else {
        return;
      }

      const container = document.getElementById(containerId);
      if (!container) return;

      // 滚动到内容区域底部，确保始终看到最新内容
      const resultElement = container.querySelector('.analysis-result');
      if (resultElement) {
        resultElement.scrollIntoView({ behavior: 'auto', block: 'end' });
      }
    };

    // 立即执行一次滚动
    scrollToGeneratingContent();
    
    // 只有在内容生成过程中才设置定时器，大幅增加滚动频率以始终跟上生成速度
    const scrollInterval = setInterval(scrollToGeneratingContent, 50);

    return () => {
      clearInterval(scrollInterval);
    };
  }, [isAnalyzing, isUserStoriesAnalyzing, isSpecificationAnalyzing]);

  // 单独跟踪分析状态的变化，只在刚完成时滚动一次
  const [prevAnalyzingState, setPrevAnalyzingState] = useState(false);
  const [prevUserStoriesAnalyzingState, setPrevUserStoriesAnalyzingState] = useState(false);
  const [prevSpecificationAnalyzingState, setPrevSpecificationAnalyzingState] = useState(false);

  // 跟踪需求分析状态变化
  useEffect(() => {
    // 只有从正在分析变为不分析（刚完成）时才滚动到底部，但不在代码生成步骤中执行
    if (prevAnalyzingState && !isAnalyzing && analysisResult && step !== 6) {
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        });
      }, 300);
    }
    
    // 更新前一个状态
    setPrevAnalyzingState(isAnalyzing);
  }, [isAnalyzing, analysisResult, prevAnalyzingState, step]);

  // 跟踪用户故事生成状态变化
  useEffect(() => {
    // 只有从正在生成变为不生成（刚完成）时才滚动到底部，但不在代码生成步骤中执行
    if (prevUserStoriesAnalyzingState && !isUserStoriesAnalyzing && userStoriesResult && step !== 6) {
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        });
      }, 300);
    }
    
    // 更新前一个状态
    setPrevUserStoriesAnalyzingState(isUserStoriesAnalyzing);
  }, [isUserStoriesAnalyzing, userStoriesResult, prevUserStoriesAnalyzingState, step]);

  // 跟踪技术规范生成状态变化
  useEffect(() => {
    // 只有从正在生成变为不生成（刚完成）时才滚动到底部，但不在代码生成步骤中执行
    if (prevSpecificationAnalyzingState && !isSpecificationAnalyzing && specificationResult && step !== 6) {
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        });
      }, 300);
    }
    
    // 更新前一个状态
    setPrevSpecificationAnalyzingState(isSpecificationAnalyzing);
  }, [isSpecificationAnalyzing, specificationResult, prevSpecificationAnalyzingState, step]);

  // 修改 startAnalysis 函数
  const startAnalysis = async (type: 'requirement' | 'user_stories' | 'specification') => {
    // 检查sessionid是否已生成
    if (!sessionId) {
      console.error('SessionId 尚未生成，无法开始分析');
      setError('系统初始化中，请稍后再试');
      return;
    }

    switch (type) {
      case 'requirement':
        setIsAnalyzing(true);
        break;
      case 'user_stories':
        setIsUserStoriesAnalyzing(true);
        break;
      case 'specification':
        setIsSpecificationAnalyzing(true);
        break;
    }

    try {
      const token = localStorage.getItem('token');
      const requestData = {
        name: formData.app_name,
        description: formData.description,
        file_ids: uploadedFiles.map(file => file.temp_file_id),
        type: type,
        requirement_content: type === 'user_stories' || type === 'specification' ? analysisResult : '',
        user_stories: type === 'specification' ? userStoriesResult : '',
        session_id: sessionId, // 添加sessionid
      };

      console.log(`开始${type}分析，session_id: ${sessionId}`);

      const response = await axios.post<{ record_id: number }>(
        'https://ceshi.lightark.cc/api/v1/flow/run-flow-with-files',
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setRecordId(response.data.record_id);

      // 滚动到内容区域
      const contentId = type === 'requirement' ? 'analysis-content' : 
                       type === 'user_stories' ? 'user-stories-content' : 
                       'specification-content';
      const element = document.getElementById(contentId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (err) {
      switch (type) {
        case 'requirement':
          setError(t('createApp.errors.analysisFailed'));
          break;
        case 'user_stories':
          setError(t('createApp.errors.userStoriesFailed'));
          break;
        case 'specification':
          setError(t('createApp.errors.specificationFailed'));
          break;
      }
      switch (type) {
        case 'requirement':
          setIsAnalyzing(false);
          break;
        case 'user_stories':
          setIsUserStoriesAnalyzing(false);
          break;
        case 'specification':
          setIsSpecificationAnalyzing(false);
          break;
      }
    }
  };

  // 轮询分析结果
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const fetchAnalysisResult = async () => {
      if (!recordId) return;

      try {
        const token = localStorage.getItem('token');
        let analysisType = '';
        if (isAnalyzing) analysisType = 'requirement';
        else if (isUserStoriesAnalyzing) analysisType = 'user_stories';
        else if (isSpecificationAnalyzing) analysisType = 'specification';
        else if (isCodeLoading) analysisType = 'web_html';
        else return;

        const response = await axios.get<FlowResponse>(
          `https://ceshi.lightark.cc/api/v1/flow/flow-results/${recordId}/${analysisType}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // 检查文本是否存在且长度足够
        if (response.data.text) {
          // 保存先前的内容
          let previousContent = '';
          const textLength = response.data.text.length;
          
          // 只有当文本长度大于30个字符时才更新状态
          if (textLength > 30) {
            switch (analysisType) {
              case 'requirement':
                previousContent = analysisResult;
                setAnalysisResult(response.data.text);
                break;
              case 'user_stories':
                previousContent = userStoriesResult;
                setUserStoriesResult(response.data.text);
                break;
              case 'specification':
                previousContent = specificationResult;
                setSpecificationResult(response.data.text);
                break;
              case 'web_html':
                previousContent = generatedCode;
                setGeneratedCode(response.data.text);
                break;
            }

            // 如果内容更新了，立即触发滚动
            if (response.data.text !== previousContent) {
              // 确定当前应该滚动的容器
              let containerId = '';
              switch (analysisType) {
                case 'requirement':
                  containerId = 'analysis-content';
                  break;
                case 'user_stories':
                  containerId = 'user-stories-content';
                  break;
                case 'specification':
                  containerId = 'specification-content';
                  break;
                case 'web_html':
                  // 页面生成时滚动到聊天底部 - 只在聊天容器内滚动
                  if (codeMessagesEndRef.current) {
                    const chatContainer = codeMessagesEndRef.current.closest('.chat-messages');
                    if (chatContainer) {
                      chatContainer.scrollTop = chatContainer.scrollHeight;
                    }
                  }
                  return;
              }

              // 立即滚动到新内容底部，确保始终看到最新生成的内容
              // 不使用延迟，直接执行滚动
              const container = document.getElementById(containerId);
              if (!container) return;

              const resultElement = container.querySelector('.analysis-result');
              if (!resultElement) return;

              // 滚动到元素底部，behavior设为auto避免平滑滚动导致的延迟
              resultElement.scrollIntoView({ behavior: 'auto', block: 'end' });
              
              // 注释掉强制滚动到页面底部，避免影响对话框布局
              // window.scrollTo(0, document.body.scrollHeight);
            }
          }
        }

        if (response.data.is_finished) {
          // 当生成完成时，无论文本长度如何，都设置最终结果
          if (response.data.text) {
            switch (analysisType) {
              case 'requirement':
                setAnalysisResult(response.data.text);
                break;
              case 'user_stories':
                setUserStoriesResult(response.data.text);
                break;
              case 'specification':
                setSpecificationResult(response.data.text);
                break;
              case 'web_html':
                setGeneratedCode(response.data.text);
                // 添加助手消息，优先使用describe字段内容
                const messageContent = response.data.describe || t('createApp.webPage.generationComplete');
                const assistantMessage: Message = {
                  type: 'assistant',
                  content: messageContent,
                  timestamp: new Date().toISOString()
                };
                setCodeMessages(prev => [...prev, assistantMessage]);
                break;
            }
          }
          
          switch (analysisType) {
            case 'requirement':
              setIsAnalyzing(false);
              break;
            case 'user_stories':
              setIsUserStoriesAnalyzing(false);
              break;
            case 'specification':
              setIsSpecificationAnalyzing(false);
              break;
            case 'web_html':
              setIsCodeLoading(false);
              break;
          }
          
          if (intervalId) {
            clearInterval(intervalId);
          }
        }
      } catch (err) {
        let errorMessage = '';
        if (isAnalyzing) {
          errorMessage = t('createApp.errors.analysisFailed');
          setIsAnalyzing(false);
        } else if (isUserStoriesAnalyzing) {
          errorMessage = t('createApp.errors.userStoriesFailed');
          setIsUserStoriesAnalyzing(false);
        } else if (isSpecificationAnalyzing) {
          errorMessage = t('createApp.errors.specificationFailed');
          setIsSpecificationAnalyzing(false);
        } else if (isCodeLoading) {
          errorMessage = t('createApp.webPage.generationFailed');
          setIsCodeLoading(false);
        }
        
        setError(errorMessage);
        if (intervalId) {
          clearInterval(intervalId);
        }
      }
    };

    if (recordId && (isAnalyzing || isUserStoriesAnalyzing || isSpecificationAnalyzing || isCodeLoading)) {
      intervalId = setInterval(fetchAnalysisResult, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [recordId, isAnalyzing, isUserStoriesAnalyzing, isSpecificationAnalyzing, isCodeLoading, t, analysisResult, userStoriesResult, specificationResult, generatedCode]);

  // 重新生成分析结果
  const regenerateAnalysis = (type: 'requirement' | 'user_stories' | 'specification') => {
    switch (type) {
      case 'requirement':
        setAnalysisResult('');
        break;
      case 'user_stories':
        setUserStoriesResult('');
        break;
      case 'specification':
        setSpecificationResult('');
        break;
    }
    setRecordId(null);
    startAnalysis(type);
  };

  // 打开编辑模态框
  const handleEditClick = (type: 'requirement' | 'user_stories' | 'specification') => {
    let content = '';
    if (type === 'requirement') {
      content = analysisResult;
    } else if (type === 'user_stories') {
      content = userStoriesResult;
    } else if (type === 'specification') {
      content = specificationResult;
    }
    
    setEditContent(content);
    setCurrentEditType(type);
    setIsEditing(true);
  };

  // 关闭编辑模态框
  const handleCloseEdit = () => {
    setIsEditing(false);
  };

  // 保存编辑内容
  const handleSaveEdit = () => {
    if (currentEditType === 'requirement') {
      setAnalysisResult(editContent);
    } else if (currentEditType === 'user_stories') {
      setUserStoriesResult(editContent);
    } else if (currentEditType === 'specification') {
      setSpecificationResult(editContent);
    }
    setIsEditing(false);
  };

  // 处理编辑内容变化
  const handleEditContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditContent(e.target.value);
  };

  // 编辑模态框组件
  const renderEditModal = () => {
    if (!isEditing) return null;

    return (
      <div className="edit-modal-overlay">
        <div className="edit-modal">
          <div className="edit-modal-header">
            <h3>{t('createApp.editModal.title')}</h3>
            <button className="close-button" onClick={handleCloseEdit}>×</button>
          </div>
          <div className="edit-modal-body">
            <textarea
              className="edit-textarea"
              value={editContent}
              onChange={handleEditContentChange}
              placeholder={t('createApp.editModal.placeholder')}
            />
          </div>
          <div className="edit-modal-footer">
            <button className="cancel-button" onClick={handleCloseEdit}>{t('createApp.editModal.cancel')}</button>
            <button className="save-button" onClick={handleSaveEdit} style={{ backgroundColor: '#1976d2', color: 'white' }}>{t('createApp.editModal.save')}</button>
          </div>
        </div>
      </div>
    );
  };

  // 添加错误提示组件
  const renderError = () => {
    if (error) {
      return (
        <div className="error-message">
          {error}
        </div>
      );
    }
    return null;
  };

  /**
   * 页面生成相关函数
   */
  // 自动滚动到聊天底部 - 只在聊天容器内滚动，避免影响整体布局
  useEffect(() => {
    if (codeMessagesEndRef.current) {
      // 找到聊天消息容器
      const chatContainer = codeMessagesEndRef.current.closest('.chat-messages');
      if (chatContainer) {
        // 只滚动聊天容器，不影响整个页面
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }
  }, [codeMessages]);

  /**
   * 处理页面生成表单提交
   * 当用户输入内容并开始生成时，自动切换到"生成代码"界面
   */
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!codeInputText.trim() || isCodeLoading) return;

    const userMessage: Message = {
      type: 'user',
      content: codeInputText,
      timestamp: new Date().toISOString()
    };

    setCodeMessages(prev => [...prev, userMessage]);
    setCodeInputText('');
    setIsCodeLoading(true);
    setCodeError(null);
    setGeneratedCode('');
    setIsPreviewMode(false);
    setPreviewUrl('');
    
    // 自动切换到"生成代码"界面
    setRightPanelMode('code');

    try {
      await callCodeStreamAPI(codeInputText);
    } catch (err) {
      setCodeError('调用API失败，请重试');
      setIsCodeLoading(false);
      console.error('API调用失败:', err);
    }
  };

  /**
   * 处理输入框回车键发送
   */
  const handleCodeInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (codeInputText.trim() && !isCodeLoading) {
        handleCodeSubmit(e as any);
      }
    }
  };

  /**
   * 调用流式API生成页面 - 修改为使用flow接口
   */
  const callCodeStreamAPI = async (userInput: string) => {
    // 检查sessionid是否已生成
    if (!sessionId) {
      console.error('SessionId 尚未生成，无法开始页面生成');
      setCodeError(t('createApp.webPage.initError'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const requestData = {
        name: formData.app_name,
        description: userInput,
        file_ids: uploadedFiles.map(file => file.temp_file_id),
        type: 'web_html',
        requirement_content: analysisResult,
        user_stories: userStoriesResult,
        specification: specificationResult,
        session_id: sessionId, // 添加sessionid
      };

      console.log(`开始页面生成，session_id: ${sessionId}`);

      const response = await axios.post<{ record_id: number }>(
        'https://ceshi.lightark.cc/api/v1/flow/run-flow-with-files',
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setRecordId(response.data.record_id);

    } catch (error) {
      console.error('流式API调用失败:', error);
      throw error;
    }
  };

  /**
   * 处理预览按钮点击
   */
  const handleCodePreview = async () => {
    if (!generatedCode.trim()) {
      setCodeError(t('createApp.webPage.noCodeToPreview'));
      return;
    }

    setIsGeneratingPreview(true);
    setCodeError(null);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch('https://ceshi.lightark.cc/api/v1/apps/generate_temp_html', {
        method: 'POST',
        headers: {
          'authorization': `Bearer ${token}`,
          'Content-Type': 'application/xml'
        },
        body: generatedCode
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: PreviewResponse = await response.json();
      const previewUrl = `https://ceshi.lightark.cc/web/${result.filename}`;
      
      setPreviewUrl(previewUrl);
      setIsPreviewMode(true);
      // 切换到预览模式
      setRightPanelMode('preview');

    } catch (error) {
      setCodeError(t('createApp.webPage.previewError'));
      console.error('预览生成失败:', error);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  /**
   * 切换右侧面板显示模式
   */
  const handleToggleRightPanel = (mode: 'code' | 'preview') => {
    if (mode === 'preview') {
      // 如果要切换到预览模式，但还没有预览内容，先生成预览
      if (!isPreviewMode && generatedCode.trim()) {
        handleCodePreview();
      } else if (isPreviewMode) {
        setRightPanelMode('preview');
      }
    } else {
      setRightPanelMode('code');
    }
  };

  /**
   * 清空页面生成对话
   */
  const handleClearCodeChat = () => {
    setCodeMessages([]);
    setGeneratedCode('');
    setIsPreviewMode(false);
    setPreviewUrl('');
    setCodeError(null);
    setRightPanelMode('code');
  };

  // 修改 renderStepContent 函数，添加错误提示和应用创建状态
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="step-content description-step">
            {renderError()}
            <div className="main-content">
              <div className="header-section">
                <h1>{t('createApp.description.title')}<br /><span className="highlight">{t('createApp.description.highlight')}</span></h1>
              </div>
              <div className="input-section">
                <input
                  type="text"
                  name="app_name"
                  value={formData.app_name}
                  onChange={handleInputChange}
                  placeholder={t('createApp.description.appName')}
                  className="app-input"
                />
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={t('createApp.description.placeholder')}
                  className="app-textarea"
                />
              </div>
            </div>
            <div className="steps-footer">
              <button className="next-button" onClick={handleNext}>
                {t('createApp.nextButton')}
              </button>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="step-content">
            {renderError()}
            <div className="header-section">
              <h1>{t('createApp.documents.title')}</h1>
              <h2>{t('createApp.documents.subtitle')}</h2>
            </div>
            <div className="upload-section">
              <div 
                className="upload-area"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="file-upload"
                  onChange={handleFileUpload}
                  accept=".txt,.md,.mdx,.pdf,.html,.xlsx,.xls,.docx,.csv,.jpg,.jpeg,.png,.gif,.webp,.svg"
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload" className="upload-button">
                  <span className="upload-icon">📁</span>
                  <span>{t('createApp.documents.dropzone')}</span>
                  <span className="upload-hint">{t('createApp.documents.formats')}</span>
                </label>
              </div>

              {isUploading && (
                <div className="upload-status">
                  <div className="custom-spinner"></div>
                  <span>{t('createApp.documents.uploading')}</span>
                </div>
              )}

              {uploadError && (
                <div className="upload-error">
                  {uploadError}
                </div>
              )}

              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  <h3>{t('createApp.documents.uploadedFiles')}</h3>
                  <div className="file-list">
                    {uploadedFiles.map((file) => (
                      <div key={file.temp_file_id} className="file-item">
                        <span className="file-icon">{getFileIcon(file.file_type)}</span>
                        <span className="file-name">{file.file_name}</span>
                        <button
                          className="remove-file"
                          onClick={() => handleRemoveFile(file.temp_file_id)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="steps-footer">
              <button className="next-button" onClick={handleNext}>
                {t('createApp.nextButton')}
              </button>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="step-content">
            {renderError()}
            <div className="developing-step">
              <div className="header-section" style={{ marginTop: '-4rem' }}>
                <h1>{t('createApp.analysis.requirements.title')}</h1>
                <h2>{t('createApp.analysis.requirements.subtitle')}</h2>
              </div>
              <div className="developing-content" id="analysis-content">
                {!isAnalyzing && !analysisResult && (
                  <button 
                    className="start-analysis-button"
                    onClick={() => startAnalysis('requirement')}
                  >
                    {t('createApp.analysis.startButton')}
                  </button>
                )}
                {isAnalyzing && !analysisResult && (
                  <div className="analyzing-status">
                    <div className="custom-spinner"></div>
                    <span>{t('createApp.analysis.analyzing')}</span>
                  </div>
                )}
                {analysisResult && (
                  <div className={`analysis-result ${isAnalyzing ? 'generating' : ''}`}>
                    <div className="analysis-actions">
                      <button 
                        className="edit-button" 
                        onClick={(e) => { e.stopPropagation(); handleEditClick('requirement'); }}
                        disabled={isAnalyzing}
                        style={{ backgroundColor: '#1976d2', color: 'white' }}
                      >
                        {t('createApp.analysis.editButton')}
                      </button>
                    </div>
                    <Markdown content={analysisResult} />
                  </div>
                )}
              </div>
            </div>
            <div className="steps-footer">
              <div className="steps-footer-buttons">
                {analysisResult && (
                  <button 
                    className="regenerate-button" 
                    onClick={() => regenerateAnalysis('requirement')}
                    disabled={isAnalyzing}
                  >
                    {t('createApp.analysis.regenerateButton')}
                  </button>
                )}
                <button 
                  className="next-button" 
                  onClick={handleNext}
                  disabled={isAnalyzing}
                >
                  {t('createApp.nextButton')}
                </button>
              </div>
            </div>
            {renderEditModal()}
          </div>
        );
      case 4:
        return (
          <div className="step-content">
            {renderError()}
            <div className="developing-step">
              <div className="header-section" style={{ marginTop: '-4rem' }}>
                <h1>{t('createApp.analysis.userStories.title')}</h1>
                <h2>{t('createApp.analysis.userStories.subtitle')}</h2>
              </div>
              <div className="developing-content" id="user-stories-content">
                {!isUserStoriesAnalyzing && !userStoriesResult && (
                  <button 
                    className="start-analysis-button"
                    onClick={() => startAnalysis('user_stories')}
                  >
                    {t('createApp.analysis.startGenerate')}
                  </button>
                )}
                {isUserStoriesAnalyzing && !userStoriesResult && (
                  <div className="analyzing-status">
                    <div className="custom-spinner"></div>
                    <span>{t('createApp.analysis.generating')}</span>
                  </div>
                )}
                {userStoriesResult && (
                  <div className={`analysis-result ${isUserStoriesAnalyzing ? 'generating' : ''}`}>
                    <div className="analysis-actions">
                      <button 
                        className="edit-button" 
                        onClick={(e) => { e.stopPropagation(); handleEditClick('user_stories'); }}
                        disabled={isUserStoriesAnalyzing}
                        style={{ backgroundColor: '#1976d2', color: 'white' }}
                      >
                        {t('createApp.analysis.editButton')}
                      </button>
                    </div>
                    <Markdown content={userStoriesResult} />
                  </div>
                )}
              </div>
            </div>
            <div className="steps-footer">
              <div className="steps-footer-buttons">
                {userStoriesResult && (
                  <button 
                    className="regenerate-button" 
                    onClick={() => regenerateAnalysis('user_stories')}
                    disabled={isUserStoriesAnalyzing}
                  >
                    {t('createApp.analysis.regenerateButton')}
                  </button>
                )}
                <button 
                  className="next-button" 
                  onClick={handleNext}
                  disabled={isUserStoriesAnalyzing}
                >
                  {t('createApp.nextButton')}
                </button>
              </div>
            </div>
            {renderEditModal()}
          </div>
        );
      case 5:
        return (
          <div className="step-content">
            {renderError()}
            <div className="developing-step">
              <div className="header-section" style={{ marginTop: '-4rem' }}>
                <h1>{t('createApp.analysis.specification.title')}</h1>
                <h2>{t('createApp.analysis.specification.subtitle')}</h2>
              </div>
              <div className="developing-content" id="specification-content">
                {!isSpecificationAnalyzing && !specificationResult && (
                  <button 
                    className="start-analysis-button"
                    onClick={() => startAnalysis('specification')}
                  >
                    {t('createApp.analysis.startGenerate')}
                  </button>
                )}
                {isSpecificationAnalyzing && !specificationResult && (
                  <div className="analyzing-status">
                    <div className="custom-spinner"></div>
                    <span>{t('createApp.analysis.generating')}</span>
                  </div>
                )}
                {specificationResult && (
                  <div className={`analysis-result ${isSpecificationAnalyzing ? 'generating' : ''}`}>
                    <div className="analysis-actions">
                      <button 
                        className="edit-button" 
                        onClick={(e) => { e.stopPropagation(); handleEditClick('specification'); }}
                        disabled={isSpecificationAnalyzing}
                        style={{ backgroundColor: '#1976d2', color: 'white' }}
                      >
                        {t('createApp.analysis.editButton')}
                      </button>
                    </div>
                    <Markdown content={specificationResult} />
                  </div>
                )}
              </div>
            </div>
            <div className="steps-footer">
              <div className="steps-footer-buttons">
                {specificationResult && (
                  <button 
                    className="regenerate-button" 
                    onClick={() => regenerateAnalysis('specification')}
                    disabled={isSpecificationAnalyzing}
                  >
                    {t('createApp.analysis.regenerateButton')}
                  </button>
                )}
                <button 
                  className="next-button" 
                  onClick={handleNext}
                  disabled={isSpecificationAnalyzing}
                >
                  {t('createApp.nextButton')}
                </button>
              </div>
            </div>
            {renderEditModal()}
          </div>
        );
      case 6:
        // 移除hasHeader动态逻辑，保持布局稳定
        return (
          <div className="step-content code-generation-step">
            {renderError()}
            {/* 分栏布局 */}
            <div className="code-generator-container">
              {/* 左侧对话面板 */}
              <div className="chat-panel">
                <div className="chat-header">
                  {/* 隐藏页面生成器标题 */}
                  {/* <h2 className="chat-title">{t('createApp.webPage.title')}</h2> */}
                  <div></div> {/* 占位元素，保持布局 */}
                  <button 
                    className="clear-button"
                    onClick={handleClearCodeChat}
                    disabled={isCodeLoading}
                  >
                    {t('createApp.webPage.clearChat')}
                  </button>
                </div>

                <div className="chat-messages">
                  {codeMessages.map((message, index) => (
                    <div key={index} className={`message ${message.type}`}>
                      {message.type === 'user' ? (
                        <div className="message-content">
                          <div className="message-text">{message.content}</div>
                          <div className="message-time">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </div>
                        </div>
                      ) : (
                        <div className="message-content">
                                                  <div className="assistant-message-container">
                          <div className="assistant-message-header">
                            <div className="coding-icon">
                              AI
                            </div>
                            <span className="assistant-label">助手</span>
                          </div>
                          <div className="assistant-message-body">
                            <div className="message-text">{message.content}</div>
                          </div>
                        </div>
                          <div className="message-time">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {isCodeLoading && (
                    <div className="message assistant">
                      <div className="message-content">
                        <div className="assistant-message-container">
                          <div className="assistant-message-header">
                            <div className="coding-icon">
                              AI
                            </div>
                            <span className="assistant-label">助手</span>
                          </div>
                          <div className="assistant-message-body">
                            <div className="loading-indicator">
                              <span>{t('createApp.webPage.generatingPage')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={codeMessagesEndRef} />
                </div>

                <form className="chat-input-form" onSubmit={handleCodeSubmit}>
                  <div className="input-container">
                    <textarea
                      value={codeInputText}
                      onChange={(e) => setCodeInputText(e.target.value)}
                      onKeyDown={handleCodeInputKeyDown}
                      placeholder={t('createApp.webPage.inputPlaceholder')}
                      disabled={isCodeLoading}
                      rows={3}
                      className="chat-input"
                    />
                    <button
                      type="submit"
                      disabled={isCodeLoading || !codeInputText.trim()}
                      className={`send-button ${isCodeLoading ? 'loading' : ''}`}
                    >
                      {isCodeLoading ? (
                        <span className="loading-icon">
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </span>
                      ) : (
                        <span className="send-icon">
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" fill="currentColor"/>
                          </svg>
                        </span>
                      )}
                    </button>
                  </div>
                </form>

                {codeError && (
                  <div className="error-message">
                    {codeError}
                  </div>
                )}
              </div>

              {/* 右侧结果面板 */}
              <div className="result-panel">
                <div className="result-header">
                  <div className="result-tabs">
                    <button
                      className={`result-tab ${rightPanelMode === 'code' ? 'active' : ''}`}
                      onClick={() => handleToggleRightPanel('code')}
                    >
                      {t('createApp.webPage.tabCode')}
                    </button>
                    <button
                      className={`result-tab ${rightPanelMode === 'preview' ? 'active' : ''}`}
                      onClick={() => handleToggleRightPanel('preview')}
                      disabled={!generatedCode.trim() || isCodeLoading}
                    >
                      {t('createApp.webPage.tabPreview')}
                    </button>
                  </div>
                  <div className="result-actions">
                    {rightPanelMode === 'code' && generatedCode && (
                      <button
                        className="preview-button"
                        onClick={handleCodePreview}
                        disabled={isGeneratingPreview || isCodeLoading}
                      >
                        {isGeneratingPreview ? t('createApp.webPage.generating') : t('createApp.webPage.generatePreview')}
                      </button>
                    )}
                    {rightPanelMode === 'preview' && isPreviewMode && (
                      <button
                        className="regenerate-button"
                        onClick={handleCodePreview}
                        disabled={isGeneratingPreview || isCodeLoading}
                      >
                        {isGeneratingPreview ? t('createApp.webPage.regenerating') : t('createApp.webPage.regenerate')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="result-content">
                  {rightPanelMode === 'code' ? (
                    <div className="code-display">
                      {generatedCode ? (
                        <pre className="code-block">
                          <code>{generatedCode}</code>
                        </pre>
                      ) : (
                        <div className="empty-state">
                          <p>{t('createApp.webPage.emptyState')}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="preview-container">
                      {isGeneratingPreview ? (
                        <div className="preview-loading">
                          <p>{t('createApp.webPage.generatingPreview')}</p>
                        </div>
                      ) : previewUrl ? (
                        <iframe
                          ref={previewIframeRef}
                          src={previewUrl}
                          className="preview-iframe"
                          title="页面预览"
                          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
                        />
                      ) : (
                        <div className="preview-placeholder">
                          <p>{t('createApp.webPage.previewPlaceholder')}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="steps-footer">
              <button 
                className="next-button" 
                onClick={handleNext}
              >
                {t('createApp.nextButton')}
              </button>
            </div>
          </div>
        );
      case 7:
        return (
          <div className="step-content">
            {renderError()}
            
            {/* 添加创建应用状态和进度指示 - 放在顶部 */}
            {isCreatingApp && !appCreationCompleted && (
              <div className="app-creating-status">
                <div className="custom-spinner"></div>
                <span>{t('createApp.creating')}</span>
                {creationProgress > 0 && (
                  <div className="progress-container">
                    <div 
                      className="progress-bar" 
                      style={{ width: `${creationProgress}%` }}
                    ></div>
                    <span className="progress-text">{creationProgress}%</span>
                  </div>
                )}
              </div>
            )}
            
            {/* 添加创建成功提示 - 放在顶部 */}
            {appCreationCompleted && (
              <div className="creation-success">
                <div className="success-icon">✓</div>
                <span>{t('createApp.success')}</span>
              </div>
            )}
            
            <div className="create-step">
              <div className="header-section">
                <h1>{t('createApp.summary.title')}<br /><span className="highlight">{t('createApp.summary.highlight')}</span></h1>
                <h2>{t('createApp.summary.subtitle')}</h2>
              </div>
              <div className="summary-container">
                <h3>{t('createApp.summary.overview')}</h3>
                <div className="summary-item">
                  <span className="label">{t('createApp.summary.appName')}：</span>
                  <span className="value">{formData.app_name}</span>
                </div>
                <div className="summary-item">
                  <span className="label">{t('createApp.summary.description')}：</span>
                  <span className="value">{formData.description}</span>
                </div>
                <div className="summary-item">
                  <span className="label">{t('createApp.summary.documents')}：</span>
                  <div className="value">
                    {uploadedFiles.length > 0 ? (
                      <div className="file-list">
                        {uploadedFiles.map((file) => (
                          <div key={file.temp_file_id} className="file-item">
                            <span className="file-icon">{getFileIcon(file.file_type)}</span>
                            <span className="file-name">{file.file_name}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span>{t('createApp.summary.noDocuments')}</span>
                    )}
                  </div>
                </div>
                <div className="summary-item">
                  <span className="label">{t('createApp.summary.requirements')}：</span>
                  <div className="value">
                    {analysisResult ? (
                      <div className="analysis-result">
                        <div className="analysis-actions">
                          <button 
                            className="edit-button" 
                            onClick={(e) => { e.stopPropagation(); handleEditClick('requirement'); }}
                            disabled={isCreatingApp}
                            style={{ backgroundColor: '#1976d2', color: 'white' }}
                          >
                            {t('createApp.analysis.editButton')}
                          </button>
                        </div>
                        <Markdown content={analysisResult} />
                      </div>
                    ) : (
                      <span>{t('createApp.summary.noAnalysis')}</span>
                    )}
                  </div>
                </div>
                <div className="summary-item">
                  <span className="label">{t('createApp.summary.userStories')}：</span>
                  <div className="value">
                    {userStoriesResult ? (
                      <div className="analysis-result">
                        <div className="analysis-actions">
                          <button 
                            className="edit-button" 
                            onClick={(e) => { e.stopPropagation(); handleEditClick('user_stories'); }}
                            disabled={isCreatingApp}
                            style={{ backgroundColor: '#1976d2', color: 'white' }}
                          >
                            {t('createApp.analysis.editButton')}
                          </button>
                        </div>
                        <Markdown content={userStoriesResult} />
                      </div>
                    ) : (
                      <span>{t('createApp.summary.noUserStories')}</span>
                    )}
                  </div>
                </div>
                <div className="summary-item">
                  <span className="label">{t('createApp.summary.specification')}：</span>
                  <div className="value">
                    {specificationResult ? (
                      <div className="analysis-result">
                        <div className="analysis-actions">
                          <button 
                            className="edit-button" 
                            onClick={(e) => { e.stopPropagation(); handleEditClick('specification'); }}
                            disabled={isCreatingApp}
                            style={{ backgroundColor: '#1976d2', color: 'white' }}
                          >
                            {t('createApp.analysis.editButton')}
                          </button>
                        </div>
                        <Markdown content={specificationResult} />
                      </div>
                    ) : (
                      <span>{t('createApp.summary.noSpecification')}</span>
                    )}
                  </div>
                </div>
                <div className="summary-item">
                  <span className="label">{t('createApp.summary.webPage')}</span>
                  <div className="value">
                    {generatedCode ? (
                      <div className="analysis-result">
                        <pre className="code-block" style={{ maxHeight: '300px', overflow: 'auto' }}>
                          <code>{generatedCode}</code>
                        </pre>
                      </div>
                    ) : (
                      <span>{t('createApp.summary.noGeneratedPage')}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="steps-footer">
              {!isCreatingApp && !appCreationCompleted && (
                <button className="next-button" onClick={handleNext}>
                  {t('createApp.createButton')}
                </button>
              )}
              {appCreationCompleted && (
                <button className="next-button" onClick={handleEnterApp}>
                  {t('createApp.enterButton')}
                </button>
              )}
            </div>
            {renderEditModal()}
          </div>
        );
      default:
        return null;
    }
  };

  // 组件卸载时清理body类名
  useEffect(() => {
    return () => {
      document.body.classList.remove('preview-mode-active');
    };
  }, []);

  // 生成sessionid的函数
  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // 组件初始化时生成sessionid
  useEffect(() => {
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    console.log('创建新的session:', newSessionId);
  }, []);

  // 处理返回应用列表
  const handleGoBack = () => {
    navigate(`/apps?workspace_id=${workspaceId}`);
  };

  return (
    <div className={`create-app-container ${isCreatingApp ? 'creating' : ''} ${step === 6 ? 'code-generation-active' : ''}`}>
      <div className="create-app-content">
        <div className="steps-indicator">
          <button className="back-button-large" onClick={handleGoBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 19-7-7 7-7"/>
              <path d="m19 12H5"/>
            </svg>
            <span>返回</span>
          </button>
          <div className="steps-wrapper">
            <div 
              className={`step-item ${step === 1 ? 'active' : ''}`} 
              onClick={() => handleStepClick(1)}
            >
              <span className="step-number">1</span>
              <span className="step-name">{t('createApp.steps.description')}</span>
            </div>
            <div 
              className={`step-item ${step === 2 ? 'active' : ''}`}
              onClick={() => handleStepClick(2)}
            >
              <span className="step-number">2</span>
              <span className="step-name">{t('createApp.steps.documents')}</span>
            </div>
            <div 
              className={`step-item ${step === 3 ? 'active' : ''}`}
              onClick={() => handleStepClick(3)}
            >
              <span className="step-number">3</span>
              <span className="step-name">{t('createApp.steps.requirements')}</span>
            </div>
            <div 
              className={`step-item ${step === 4 ? 'active' : ''}`}
              onClick={() => handleStepClick(4)}
            >
              <span className="step-number">4</span>
              <span className="step-name">{t('createApp.steps.userStories')}</span>
            </div>
            <div 
              className={`step-item ${step === 5 ? 'active' : ''}`}
              onClick={() => handleStepClick(5)}
            >
              <span className="step-number">5</span>
              <span className="step-name">{t('createApp.steps.specification')}</span>
            </div>
            <div 
              className={`step-item ${step === 6 ? 'active' : ''}`}
              onClick={() => handleStepClick(6)}
            >
              <span className="step-number">6</span>
              <span className="step-name">{t('createApp.steps.webPage')}</span>
            </div>
            <div 
              className={`step-item ${step === 7 ? 'active' : ''}`}
              onClick={() => handleStepClick(7)}
            >
              <span className="step-number">7</span>
              <span className="step-name">{t('createApp.steps.create')}</span>
            </div>
          </div>
        </div>
        {renderStepContent()}
      </div>
    </div>
  );
};

export default CreateApp; 